import React, { useRef, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./Onboarding.css";
import { useAuraVoice } from "../../hooks/useAuraVoice";
import { sendUserAudio } from "../../services/api";

// Importando os assets existentes
import Stars from "../../assets/stars.svg";
import Mountain from "../../assets/mountain2.svg";
import Logo from "../../assets/logo.svg";
import { MdKeyboardArrowUp } from "react-icons/md";
import Mic from "../../assets/mic.svg";
import Stop from "../../assets/stop.svg";
import { useGlobal } from "../../context/GlobalContext";

const MAX_HEIGHT = 150;

const Onboarding = ({ value, onChange } = {}) => {
  const [text, setText] = useState(value ?? "");
  const [error, setError] = useState("");

  const [recStatus, setRecStatus] = useState("idle"); // idle | recording | uploading
  const [hint, setHint] = useState("");               // small UX hint text

  const navigate = useNavigate();
  const { dispatch } = useGlobal();
  useAuraVoice("Oi! Prazer em te conhecer. Como posso te chamar?", "question");

  const taRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const validateInput = (value) => {
    if (value.trim().length === 0) {
      return false;
    }
    return true;
  };

  const handleTextChange = (v) => {
    if (error) setError("");
    setText(v);
    // Safely call parent handler if present
    if (typeof onChange === "function") onChange(v);
  };

  const resize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = next + "px";
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  };
  
  useLayoutEffect(() => {
    // keep your resize() logic
    resize();
  }, [text]); // watch local text, not prop value


  const handleSubmit = () => {
    const isInputValid = text.trim().length > 0;
    if (isInputValid) {
      setError("");
      dispatch({ type: "SET_USER_NAME", payload: text });
      navigate("/problem-description");
    } else {
      setError("Por favor, preencha seu nome.");
    }
  };

  // --- MIC LOGIC -------------------------------------------------------------
  
    const getMimeType = () => {
      if (MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus")) {
        return "audio/webm;codecs=opus";
      }
      if (MediaRecorder.isTypeSupported?.("audio/webm")) {
        return "audio/webm";
      }
      // Safari support for MediaRecorder is still evolving; we’ll still try webm.
      return "";
    };
  
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mimeType = getMimeType();
        const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  
        chunksRef.current = [];
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        mr.onstop = handleStop;
        mr.start();
  
        mediaRecorderRef.current = mr;
        setRecStatus("recording");
        setHint("Gravando… clique para parar");
      } catch (err) {
        console.error(err);
        setHint("Não foi possível acessar o microfone.");
      }
    };
  
    const stopRecording = () => {
      try {
        mediaRecorderRef.current?.stop();
        mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
      } catch {}
      setRecStatus("uploading");
      setHint("Enviando áudio…");
    };
  
    const handleStop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];
      try {
        const file = new File([blob], "mic.webm", { type: "audio/webm" });
        const form = new FormData();
        form.append("audio", file, "mic.webm");
        form.append("language", "pt");

        const result = await sendUserAudio(form);
        const transcript = typeof result === "string"
          ? result
          : (result?.transcript || result?.text || "");

        if (transcript) {
          // update local state
          setText(transcript);
          // notify parent only if valid
          if (typeof onChange === "function") onChange(transcript);
          setHint("Transcrição recebida.");
        } else {
          setHint("Áudio enviado, mas não recebi texto.");
        }
      } catch (err) {
        console.error(err);
        setHint("Falha ao enviar/interpretar o áudio.");
      } finally {
        setRecStatus("idle");
        setTimeout(() => setHint(""), 2500);
      }
    };
  
    const toggleMic = () => {
      if (recStatus === "recording") {
        stopRecording();
      } else if (recStatus === "idle") {
        startRecording();
      }
    };
  
    // --------------------------------------------------------------------------

  return (
    <motion.div
              className="onboarding-container"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
      <img src={Stars} alt="Estrelas no céu" className="stars-background" />
      <img src={Mountain} alt="Montanhas" className="mountain-background" />

      <main className="onboarding-content">
        <motion.img 
          src={Logo} 
          alt="Logo" 
          className="onboarding-logo"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.8, 
            ease: "easeOut" 
          }}
        />
        <motion.h1 
          className="onboarding-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: "easeOut", 
            delay: 1.0 
          }}
        >
          Como posso te chamar?
        </motion.h1>

        <div className="input-container">
          <textarea
            ref={taRef}
            placeholder="Diga seu nome"
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onInput={resize}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            rows={1}
            aria-label="Descrição do desafio"
          />
          <div className="input-icons">
            {/* MIC BUTTON */}
              <button
                type="button"
                className={`mic-btn ${recStatus}`}
                onClick={toggleMic}
                aria-label={recStatus === "recording" ? "Parar gravação" : "Gravar áudio"}
                disabled={recStatus === "uploading"}
                title={recStatus === "recording" ? "Parar" : "Gravar"}
              >
                {recStatus === "recording" ? <img src={Stop} className="mic-icon"/> : <img src={Mic} className="mic-icon"/>}
              </button>
  
              {/* SUBMIT ARROW */}
              <div className="arrow-icon-container" onClick={handleSubmit}>
                <MdKeyboardArrowUp className="arrow-icon" />
              </div>
          </div>
        </div>
        {/* {error && <p className="error-message">{error}</p>} */}
        
        {/* Tiny status hint under input */}
        {(hint || error) && (
          <p className={error ? "error-message" : "hint-message"}>
            {error || hint}
          </p>
        )}
      </main>
    </motion.div>
  );
};

export default Onboarding;
